import type { VoidComponent } from "solid-js";
import { createMemo, Show } from "solid-js";
import type { ColumnMappingDto, DataSourceInfoDto } from "../services/ApiService";
import ApiService from "../services/ApiService";
import { useFormVersionState } from "../state/formVersion";
import { PropertiesAccordion } from "../components/accordion/Accordion";
import CheckboxListItem from "../components/listItem/CheckboxListItem";
import { isNil } from "lodash-es";
import { useTranslation } from "../state/i18n";
import type { SingleSelectInputProps } from "../components/menu/Select";
import type { SelectOption } from "../types/Menu";
import { SingleSelectInput } from "../components/menu/Select";
import PermissionService from "../services/PermissionService";
import { useCustomerState } from "../state/customer";
import InlineMessage from "../components/inlineMessage/InlineMessage";

type DataSourceConfiguration = {
  id: string;
  mapping: Record<string, boolean>;
};
type DataSourceInputProps = Omit<SingleSelectInputProps<DataSourceConfiguration>, "options"> & {
  onSelect: (value: DataSourceConfiguration | undefined) => void;
};

type DataSourceOption = SelectOption<string> & { columns: ColumnMappingDto[] };

export const mapDataSourceInfoDtoToOption = (dataSourceInfo: DataSourceInfoDto): DataSourceOption => ({
  variant: "text",
  value: dataSourceInfo.id,
  label: dataSourceInfo.name,
  columns: dataSourceInfo.columnMapping,
});

const DataSourceInput: VoidComponent<DataSourceInputProps> = (props) => {
  const { t } = useTranslation();
  const { formId } = useFormVersionState();
  const customer = useCustomerState();
  const canManageDataSources = PermissionService.canManageDataSources(customer.customerId);
  const dataSourceInfosQuery = ApiService.getDataSourceInfosQuery(formId);
  const options = createMemo<DataSourceOption[]>(() => {
    const dataSources = dataSourceInfosQuery.data ?? [];
    const visible = canManageDataSources ? dataSources : dataSources.filter(({ id }) => id === props.value?.id);
    return visible.map(mapDataSourceInfoDtoToOption);
  });
  const currentDataSource = createMemo(() => options().find((dataSource) => dataSource.value === props.value?.id));
  const dataSourceColumns = createMemo(() => {
    const data = currentDataSource();
    if (isNil(data)) {
      return [];
    }

    if (canManageDataSources) {
      return data.columns;
    }
    return data.columns.filter(({ id }) => props.value?.mapping[id] ?? false);
  });
  const disabled = createMemo(() => props.disabled === true || !canManageDataSources);

  const getMapping = (id: string): Record<string, boolean> => {
    const columns = options().find((dataSource) => dataSource.value === id)?.columns;
    if (isNil(columns)) {
      return {};
    }

    return columns.reduce((record: Record<string, boolean>, column) => {
      record[column.id] = true;
      return record;
    }, {});
  };

  return (
    <Show when={dataSourceInfosQuery.status === "success"}>
      <div class="flex flex-col gap-y-2">
        <SingleSelectInput
          {...props}
          value={props.value?.id}
          onSelect={(value) => props.onSelect(value ? { id: value, mapping: getMapping(value) } : undefined)}
          options={options()}
          sameWidth
          disabled={disabled()}
        />
        <Show when={!canManageDataSources}>
          <InlineMessage variant="warning" label={t(($) => $.DATA_SOURCE_NO_PERMISSION_WARNING)} />
        </Show>
        <PropertiesAccordion
          variant="list"
          items={[
            {
              label: t(($) => $.DATA_SOURCE_SELECT_COLUMNS),
              key: "columns",
              components:
                dataSourceColumns().map((column) => ({
                  Component: () => (
                    <CheckboxListItem
                      label={column.label}
                      disabled={disabled()}
                      checked={props.value?.mapping[column.id] ?? false}
                      setChecked={(value) => {
                        if (isNil(props.value)) {
                          return;
                        }
                        props.onSelect({
                          id: props.value.id,
                          mapping: {
                            ...props.value.mapping,
                            [column.id]: value,
                          },
                        });
                      }}
                    />
                  ),
                  filterKey: column.label,
                })) ?? [],
            },
          ]}
        />
      </div>
    </Show>
  );
};

export default DataSourceInput;
